import { useQuery } from "@tanstack/react-query";
import { rolesLookupApi, type RoleDto } from "@/api/lookups";

/**
 * Admin-managed Lookups v1.0 §4d — single source of truth for the
 * user-facing active roles list. Every place that renders a role label
 * (RoleChip, dropdowns, chip groups, badges) uses this hook so changes
 * made via <c>/admin/roles</c> propagate everywhere via React Query
 * cache invalidation.
 *
 * Five-minute staleTime per spec §8 — roles change rarely; refetching
 * on every dropdown render is wasteful. When an admin mutates a role,
 * the admin page invalidates the <c>['roles']</c> query key, which
 * forces an immediate refetch in every active session.
 */
export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesLookupApi.list(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Admin variant — includes archived rows so the <c>/admin/roles</c>
 * list view can render the "Show archived" toggle. Separate cache key
 * so the user-facing query isn't bloated with archived data.
 */
export function useAdminRoles(includeArchived: boolean) {
  return useQuery({
    queryKey: ["admin-roles", includeArchived],
    queryFn: () => rolesLookupApi.adminList(includeArchived),
  });
}

/**
 * Resolves a role code to its display name. Returns the code itself as
 * the fallback while the query is loading or for codes that no longer
 * exist in the active list (e.g. archived roles still referenced by
 * historical data).
 */
export function useRoleLabel(code: string): {
  isLoading: boolean;
  label: string;
  role: RoleDto | undefined;
} {
  const { data, isLoading } = useRoles();
  const role = data?.find((r) => r.code === code);
  return {
    isLoading,
    label: role?.displayName ?? code,
    role,
  };
}

/**
 * Returns a memoizable resolver for callers that need to look up many
 * codes at once (table rows, chip lists). Avoids repeated <c>find</c>
 * scans by closing over a code-keyed dictionary.
 */
export function useRoleLabels(): {
  isLoading: boolean;
  titleOf: (code: string) => string | undefined;
} {
  const { data, isLoading } = useRoles();
  const lookup = data?.reduce<Record<string, string>>((acc, r) => {
    acc[r.code] = r.displayName;
    return acc;
  }, {}) ?? {};
  return {
    isLoading,
    titleOf: (code) => lookup[code],
  };
}
